import * as plugins from './smarttime.plugins.js';

import { TimeStamp } from './smarttime.classes.timestamp.js';

export type TimerState = 'initiated' | 'started' | 'paused' | 'completed';

export class Timer {
  /**
   * the original amount of milliseconds for this Timer
   */
  public timeInMilliseconds: number;

  /**
   * the state of the timer
   */
  public state: TimerState = 'initiated';

  /**
   * completed Promise
   */
  public completed: Promise<void>;

  /**
   * a reference to when the Timeout started
   */
  public startedAt: TimeStamp | null = null;

  /**
   * a reference to when a Timer has been potentially paused
   */
  public pausedAt: TimeStamp | null = null;

  get timeLeft(): number {
    const elapsedAtPause = this.pausedAt?.change;
    if (elapsedAtPause === undefined || elapsedAtPause === null) {
      throw new Error('timer has NOT been paused before. timeLeft is known after .pause()');
    }
    return this.timeInMilliseconds - elapsedAtPause;
  }
  /**
   * the current timeout the needs to be canceled when this Timer is stopped
   */
  private currentTimeout: plugins.smartpromise.Timeout<void> | null = null;

  // a deferred triggeted when Timer has completed
  private completedDeferred = plugins.smartpromise.defer<void>();

  constructor(timeInMillisecondsArg: number) {
    this.timeInMilliseconds = timeInMillisecondsArg;
    this.completed = this.completedDeferred.promise;
  }

  /**
   * starts the timer
   */
  public start() {
    if (!this.startedAt) {
      this.startTimeout(this.timeInMilliseconds);
      this.startedAt = new TimeStamp();
    } else {
      throw new Error('timer has been started before. Please use resume instead');
    }
  }

  public pause() {
    if (this.startedAt) {
      this.currentTimeout?.cancel();
      this.currentTimeout = null;
      this.pausedAt = TimeStamp.fromTimeStamp(this.startedAt);
    }
  }

  public resume() {
    if (this.startedAt) {
      this.startTimeout(this.timeLeft);
    } else {
      throw new Error('timer has NOT been started before. Please use .start() instead');
    }
  }

  public reset() {
    this.pause();
    this.startedAt = null;
    this.pausedAt = null;
  }

  /**
   * starts the one live timeout of this Timer, replacing any earlier one;
   * smartpromise's Timeout also waits out delays beyond the setTimeout ceiling
   */
  private startTimeout(timeInMillisecondsArg: number) {
    this.currentTimeout?.cancel();
    const timeout = new plugins.smartpromise.Timeout<void>(timeInMillisecondsArg);
    this.currentTimeout = timeout;
    timeout.promise.then(() => {
      this.completedDeferred.resolve();
    });
  }
}
